import { FilteringProperty } from "@amzn/awsui-components-react/polaris/property-filter/interfaces";
import {
    compareNullableNumbers,
    NULLABLE_NUMBER_OPERATORS,
    columnDefinitionToOption,
    configPropsToColumnDefinitions,
    configPropsToFilterProps, 
    formatDecimal,
    NUMBER_FIELD_OPERATORS,
    TableContentConfigProps,
    TEXT_FIELD_OPERATORS
} from "../tableConfig";
import CopyTextField from "../../components/offline-maps/CopyTextField";
import {CollectionPreferencesProps} from "@amzn/awsui-components-react";
import { comparePnfNumbers, PNF_NUMBER_OPERATORS, RecostPnfTotals } from "./recostPnfMetrics";
import RecostPnfCount from "../../components/mapRelease/RecostPnfCount";

import { StationRecostLatency, RecostLatencyChanges } from "./recostLatencyMetrics";
import RecostLatencyChange from "../../components/mapRelease/RecostLatencyChange";

export interface StationRecostingData extends RecostPnfTotals, StationRecostLatency, RecostLatencyChanges {
    stationCode: string;
    country_code: string;

    overall_tt_breached: boolean;
    stem_tt_breached: boolean;
    trip_count: number;
    failure_count_control: number;
    failure_count_experiment: number;
    failure_trip_count: number;
    total_distance_control: number;
    total_distance_delta_percent_avg: number;
    total_distance_experiment: number;
    total_time_control: number;
    total_time_delta_percent_avg: number;
    total_time_experiment: number;

    at_station_trip_count: number;
    at_station_trip_failure_count_control: number;
    at_station_trip_failure_count_experiment: number;

    on_zone_failure_trip_count: number;
    on_zone_pnf_failure_control_count: number | null;
    on_zone_pnf_failure_experiment_count: number | null;
    on_zone_snap_failure_control_count: number;
    on_zone_snap_failure_experiment_count: number;
    on_zone_total_distance_control: number;
    on_zone_total_distance_delta_percent_av: number;
    on_zone_trip_count: number;
    on_zone_total_time_experiment: number;
    on_zone_total_time_delta_percent_avg: number;
    on_zone_total_time_control: number;
    on_zone_total_distance_experiment: number;

    stem_in_failure_trip_count: number;
    stem_in_pnf_failure_control_count: number | null;
    stem_in_pnf_failure_experiment_count: number | null;
    stem_in_snap_failure_control_count: number;
    stem_in_snap_failure_experiment_count: number;
    stem_in_total_distance_control: number;
    stem_in_total_distance_delta_percent_avg: number;
    stem_in_total_distance_experiment: number;
    stem_in_total_time_control: number;
    stem_in_total_time_delta_percent_avg: number;
    stem_in_total_time_experiment: number;
    stem_in_trip_count: number;

    stem_out_failure_trip_count: number;
    stem_out_pnf_failure_control_count: number | null;
    stem_out_pnf_failure_experiment_count: number | null;
    stem_out_snap_failure_control_count: number;
    stem_out_snap_failure_experiment_count: number;
    stem_out_total_distance_control: number;
    stem_out_total_distance_delta_percent_avg: number;
    stem_out_total_distance_experiment: number;
    stem_out_total_time_control: number;
    stem_out_total_time_delta_percent_avg: number;
    stem_out_total_time_experiment: number;
    stem_out_trip_count: number;
}

const STATION_RECOSTING_SUMMARY_TABLE_CONTENT_CONFIG: TableContentConfigProps<StationRecostingData>[] =
    [
        {
            id: "stationCode",
            description: "Station",
            width: 110,
            minWidth: 110,
            maxWidth: 110,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stationCode} />
            ),
            sortingField: "stationCode",
            filterGroup: "stationRecostingTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "country_code",
            description: "Country",
            width: 110,
            minWidth: 110,
            maxWidth: 110,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.country_code} />
            ),
            sortingField: "country_code",
            filterGroup: "stationRecostingTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "trip_count",
            description: "Trips",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.trip_count} />
            ),
            sortingField: "trip_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "latencyP50ChangePercent",
            description: "P50 Latency Change (%)",
            width: 200,
            minWidth: 170,
            maxWidth: 220,
            tableCell: (row: StationRecostingData) => (
                <RecostLatencyChange change={row.latencyP50ChangePercent}
                    control={row.client_latency_p50_control_ms} candidate={row.client_latency_p50_candidate_ms}
                    pairedCount={row.client_latency_paired_count} recordCount={row.client_latency_record_count}/>
            ),
            sortingComparator: (lhs, rhs) => compareNullableNumbers(lhs.latencyP50ChangePercent, rhs.latencyP50ChangePercent),
            filterGroup: "stationRecostingTableProp",
            filterOperators: NULLABLE_NUMBER_OPERATORS,
        },
        {
            id: "latencyP99ChangePercent",
            description: "P99 Latency Change (%)",
            width: 200,
            minWidth: 170,
            maxWidth: 220,
            tableCell: (row: StationRecostingData) => (
                <RecostLatencyChange change={row.latencyP99ChangePercent}
                    control={row.client_latency_p99_control_ms} candidate={row.client_latency_p99_candidate_ms}
                    pairedCount={row.client_latency_paired_count} recordCount={row.client_latency_record_count}/>
            ),
            sortingComparator: (lhs, rhs) => compareNullableNumbers(lhs.latencyP99ChangePercent, rhs.latencyP99ChangePercent),
            filterGroup: "stationRecostingTableProp",
            filterOperators: NULLABLE_NUMBER_OPERATORS,
        },
        {
            id: "totalPnfCountControl",
            description: "Total PNF Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.totalPnfCountControl} />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.totalPnfCountControl, rhs.totalPnfCountControl),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "totalPnfCountCandidate",
            description: "Total PNF Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.totalPnfCountCandidate} control={stationRecostingData.totalPnfCountControl} />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.totalPnfCountCandidate, rhs.totalPnfCountCandidate),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "pnfNetChange",
            description: "PNF Net Change",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.pnfNetChange} signed />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.pnfNetChange, rhs.pnfNetChange),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "on_zone_pnf_failure_control_count",
            description: "On-zone PNF Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.on_zone_pnf_failure_control_count} />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.on_zone_pnf_failure_control_count, rhs.on_zone_pnf_failure_control_count),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "on_zone_pnf_failure_experiment_count",
            description: "On-zone PNF Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.on_zone_pnf_failure_experiment_count} control={stationRecostingData.on_zone_pnf_failure_control_count} />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.on_zone_pnf_failure_experiment_count, rhs.on_zone_pnf_failure_experiment_count),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "stem_in_pnf_failure_control_count",
            description: "Stem-in PNF Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.stem_in_pnf_failure_control_count} copyable />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.stem_in_pnf_failure_control_count, rhs.stem_in_pnf_failure_control_count),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "stem_in_pnf_failure_experiment_count",
            description: "Stem-in PNF Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.stem_in_pnf_failure_experiment_count} control={stationRecostingData.stem_in_pnf_failure_control_count} copyable />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.stem_in_pnf_failure_experiment_count, rhs.stem_in_pnf_failure_experiment_count),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "stem_out_pnf_failure_control_count",
            description: "Stem-out PNF Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.stem_out_pnf_failure_control_count} copyable />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.stem_out_pnf_failure_control_count, rhs.stem_out_pnf_failure_control_count),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "stem_out_pnf_failure_experiment_count",
            description: "Stem-out PNF Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <RecostPnfCount value={stationRecostingData.stem_out_pnf_failure_experiment_count} control={stationRecostingData.stem_out_pnf_failure_control_count} copyable />
            ),
            sortingComparator: (lhs, rhs) => comparePnfNumbers(lhs.stem_out_pnf_failure_experiment_count, rhs.stem_out_pnf_failure_experiment_count),
            filterGroup: "stationRecostingTableProp",
            filterOperators: PNF_NUMBER_OPERATORS,
        },
        {
            id: "overall_tt_breached",
            description: "Overall TT Breach (4%)",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.overall_tt_breached.toString()} />
            ),
            sortingField: "overall_tt_breached",
            filterGroup: "stationRecostingTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "stem_tt_breached",
            description: "Stem TT Breach (10%)",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_tt_breached.toString()} />
            ),
            sortingField: "stem_tt_breached",
            filterGroup: "stationRecostingTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "total_time_delta_percent_avg",
            description: "Total Time Change %",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={formatDecimal(stationRecostingData.total_time_delta_percent_avg)} />
            ),
            sortingField: "total_time_delta_percent_avg",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_in_total_time_delta_percent_avg",
            description: "Stem-in Time Change %",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={formatDecimal(stationRecostingData.stem_in_total_time_delta_percent_avg)} />
            ),
            sortingField: "stem_in_total_time_delta_percent_avg",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_out_total_time_delta_percent_avg",
            description: "Stem-out Time Change %",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={formatDecimal(stationRecostingData.stem_out_total_time_delta_percent_avg)} />
            ),
            sortingField: "stem_out_total_time_delta_percent_avg",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "total_distance_delta_percent_avg",
            description: "Total Distance Change %",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={formatDecimal(stationRecostingData.total_distance_delta_percent_avg)} />
            ),
            sortingField: "total_distance_delta_percent_avg",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "failure_count_control",
            description: "All Failures Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.failure_count_control} />
            ),
            sortingField: "failure_count_control",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "failure_count_experiment",
            description: "All Failures Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.failure_count_experiment} />
            ),
            sortingField: "failure_count_experiment",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "failure_trip_count",
            description: "Failure Trip Count",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.failure_trip_count} />
            ),
            sortingField: "failure_trip_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "on_zone_failure_trip_count",
            description: "On-zone Failure Trip Count",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.on_zone_failure_trip_count} />
            ),
            sortingField: "on_zone_failure_trip_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_in_failure_trip_count",
            description: "Stem-in Failure Trip Count",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_in_failure_trip_count} />
            ),
            sortingField: "stem_in_failure_trip_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_out_failure_trip_count",
            description: "Stem-out Failure Trip Count",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_out_failure_trip_count} />
            ),
            sortingField: "stem_out_failure_trip_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_in_snap_failure_control_count",
            description: "Stem-in Snap Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_in_snap_failure_control_count} />
            ),
            sortingField: "stem_in_snap_failure_control_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_in_snap_failure_experiment_count",
            description: "Stem-in Snap Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_in_snap_failure_experiment_count} />
            ),
            sortingField: "stem_in_snap_failure_experiment_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_out_snap_failure_control_count",
            description: "Stem-out Snap Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_out_snap_failure_control_count} />
            ),
            sortingField: "stem_out_snap_failure_control_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_out_snap_failure_experiment_count",
            description: "Stem-out Snap Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_out_snap_failure_experiment_count} />
            ),
            sortingField: "stem_out_snap_failure_experiment_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "total_time_control",
            description: "Total Time Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={formatDecimal(stationRecostingData.total_time_control)} />
            ),
            sortingField: "total_time_control",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "total_time_experiment",
            description: "Total Time Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={formatDecimal(stationRecostingData.total_time_experiment)} />
            ),
            sortingField: "total_time_experiment",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "total_distance_control",
            description: "Total Distance Control",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={formatDecimal(stationRecostingData.total_distance_control)} />
            ),
            sortingField: "total_distance_control",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "total_distance_experiment",
            description: "Total Distance Candidate",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={formatDecimal(stationRecostingData.total_distance_experiment)} />
            ),
            sortingField: "total_distance_experiment",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "on_zone_trip_count",
            description: "On-zone Trips",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.on_zone_trip_count} />
            ),
            sortingField: "on_zone_trip_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_in_trip_count",
            description: "Stem-in Trips",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_in_trip_count} />
            ),
            sortingField: "stem_in_trip_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
        {
            id: "stem_out_trip_count",
            description: "Stem-out Trips",
            width: 140,
            minWidth: 140,
            maxWidth: 140,
            tableCell: (stationRecostingData: StationRecostingData) => (
                <CopyTextField content={stationRecostingData.stem_out_trip_count} />
            ),
            sortingField: "stem_out_trip_count",
            filterGroup: "stationRecostingTableProp",
            filterOperators: NUMBER_FIELD_OPERATORS,
        },
    ];

export const STATION_RECOSTING_SUMMARY_TABLE_COLUMN_DEFINITIONS =
    configPropsToColumnDefinitions(STATION_RECOSTING_SUMMARY_TABLE_CONTENT_CONFIG);

// Absolute costs and category trip volumes remain in the picker and full CSV download.
export const STATION_RECOSTING_SUMMARY_DEFAULT_VISIBLE_COLUMNS =
    STATION_RECOSTING_SUMMARY_TABLE_COLUMN_DEFINITIONS.map((column) => column.id!)
        .filter((id) => ![
            "total_time_control", "total_time_experiment", "total_distance_control", "total_distance_experiment",
            "on_zone_trip_count", "stem_in_trip_count", "stem_out_trip_count"
        ].includes(id));

export const STATION_RECOSTING_SUMMARY_TABLE_VISIBLE_CONTENT_OPTIONS: any = [
    {
        label: "Station Recosting Summary",
        options: columnDefinitionToOption(STATION_RECOSTING_SUMMARY_TABLE_COLUMN_DEFINITIONS),
    },
];

export const STATION_RECOSTING_SUMMARY_TABLE_FILTER_PROPERTIES: FilteringProperty[] =
    configPropsToFilterProps(STATION_RECOSTING_SUMMARY_TABLE_CONTENT_CONFIG).sort((a, b) =>
        a.propertyLabel.localeCompare(b.propertyLabel)
    );

export const PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizeOption[] = [
    { value: 25, label: "25 Entries" },
    { value: 50, label: "50 Entries" },
    { value: 100, label: "100 Entries" },
];